import type { SlateBlock, SlateLeaf } from '@atlas/renderer';
import type { KeyboardEvent } from 'react';
import { Transforms, Text, Editor } from 'slate';
import locateTextBefore from '@atlas/tool/box/slate/locateTextBefore';
import getNode from '@atlas/tool/box/slate/getNode';
import type { MarkName } from '@atlas/tool/box/slate/isMarkActive';
import changeEditorSelection from '@atlas/tool/box/slate/changeEditorSelection';

export default function handleInlineFormattingKeyDownEvent(
    editor: Editor,
    event: KeyboardEvent,
    symbol: string,
    mark: MarkName,
): boolean {
    if (symbol.length !== 1) return false;
    if (!editor.selection) return false;
    if (event.key !== symbol) return false;

    const { anchor, focus } = editor.selection;
    if (anchor.offset !== focus.offset || anchor.path.some((p, i) => p !== focus.path[i])) return false;

    const node = getNode<SlateBlock>(editor, anchor.path.slice(0, -1));
    if (!node || !('children' in node)) return false;

    const boldPosition = locateTextBefore(
        editor,
        new RegExp(`\\${symbol}[^\\s\\${symbol}][^\\n\\${symbol}]*$`),
        undefined,
        { requireNoSymbolsBefore: symbol },
    );
    if (!boldPosition) return false;

    const lastLeaf = node.children[boldPosition.focus.path.slice(-1)[0]] as SlateLeaf;

    if (node.type === 'code-block' || lastLeaf.code) return false;

    event.preventDefault();

    Transforms.delete(editor, {
        at: {
            anchor: boldPosition.anchor,
            focus: { ...boldPosition.anchor, offset: boldPosition.anchor.offset + 1 },
        },
    });

    Transforms.setNodes(
        editor,
        { [mark]: true },
        {
            match: Text.isText,
            at: {
                anchor: boldPosition.anchor,
                focus: editor.selection.focus,
            },
            split: true,
        },
    );

    const newPosition = editor.selection.focus;
    const block = getNode<SlateBlock & { children: SlateLeaf[] }>(editor, newPosition.path.slice(0, -1));

    if (!block) {
        Transforms.insertNodes(editor, { ...lastLeaf, text: ' ' });
    } else {
        const index = newPosition.path[newPosition.path.length - 1];
        if (block.children.length - 1 === index && block.children[index].text.length === newPosition.offset) {
            Transforms.insertNodes(editor, { ...lastLeaf, text: ' ' });
        } else {
            const newPoint = {
                offset: 0,
                path: newPosition.path.slice(0, -1).concat(index),
            };
            changeEditorSelection(editor, {
                anchor: newPoint,
                focus: newPoint,
            });
        }
    }

    return true;
}

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('handleInlineFormattingKeyDownEvent', async () => {
        it.todo('🤷🏼');
    });
}

// #endregion
